/* eslint-disable react-hooks/exhaustive-deps */
import { StatusCode } from '@/api/enumerations';
import {
  getOversightNotifications,
  readOversightNotification,
} from '@/api/oversight';
import { OversightNotification } from '@/api/oversight/types';
import { formatDateLongMonth, formatFirstLetter } from '@/helpers';
import { useDebounce } from '@/hooks/useDebounce';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { normalizeStr } from '@/pages/Oversight/utils';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Constants } from '../../../../constants/oversight';
import { NotificationFilterValue } from './data';

export interface NotificationGroup {
  label: string;
  items: OversightNotification[];
}

interface UseNotificationsHook {
  search: string;
  setSearch: (value: string) => void;
  activeFilter: NotificationFilterValue;
  setActiveFilter: (value: NotificationFilterValue) => void;
  groups: NotificationGroup[];
  loading: boolean;
  hasMore: boolean;
  handleLoadMore: () => void;
  handleRead: (notification: OversightNotification) => Promise<void>;
  handleOpenClient: (clientId: number) => void;
}

const groupLabel = (occurredAt: string): string => {
  const date = dayjs(occurredAt);

  if (date.isSame(dayjs(), 'day')) return Constants.notifications_today;

  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) {
    return Constants.notifications_yesterday;
  }

  return formatFirstLetter(formatDateLongMonth(occurredAt));
};

export function useNotifications(onRead: () => void): UseNotificationsHook {
  const [notifications, setNotifications] = useState<OversightNotification[]>(
    []
  );
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] =
    useState<NotificationFilterValue>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const debouncedSearch = useDebounce(search);

  const getNotifications = async (pageToLoad: number): Promise<void> => {
    setLoading(true);
    try {
      const { data, detail } = await getOversightNotifications(
        normalizeStr(debouncedSearch.trim()),
        activeFilter === 'all' ? undefined : activeFilter,
        pageToLoad
      );

      if (detail?.description) throw new Error(detail.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (!data) throw new Error('Algo deu errado, tente novamente.');

      setNotifications((prev) =>
        pageToLoad === 1 ? data.notifications : [...prev, ...data.notifications]
      );
      setTotalPages(data.pages);
      setPage(pageToLoad);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotifications(1);
  }, [debouncedSearch, activeFilter]);

  const handleLoadMore = useCallback(() => {
    getNotifications(page + 1);
  }, [page, debouncedSearch, activeFilter]);

  const handleRead = useCallback(
    async (notification: OversightNotification): Promise<void> => {
      if (notification.read_at) return;

      try {
        const { data, detail } = await readOversightNotification(
          notification.id
        );

        if (detail?.description) throw new Error(detail.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notification.id
              ? { ...item, read_at: data?.read_at ?? dayjs().toISOString() }
              : item
          )
        );
        onRead();
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [handleSnackbar, getErrorMessage, onRead]
  );

  const handleOpenClient = useCallback(
    (clientId: number): void => {
      navigate(`/oversight/clients/customer-details/${clientId}`);
    },
    [navigate]
  );

  const groups = useMemo(
    () =>
      [...notifications]
        .sort(
          (a, b) =>
            dayjs(b.occurred_at).valueOf() - dayjs(a.occurred_at).valueOf()
        )
        .reduce<NotificationGroup[]>((acc, item) => {
          const label = groupLabel(item.occurred_at);
          const current = acc[acc.length - 1];

          if (current?.label === label) {
            current.items.push(item);
            return acc;
          }

          acc.push({ label, items: [item] });
          return acc;
        }, []),
    [notifications]
  );

  return {
    search,
    setSearch,
    activeFilter,
    setActiveFilter,
    groups,
    loading,
    hasMore: page < totalPages,
    handleLoadMore,
    handleRead,
    handleOpenClient,
  };
}
